import styled from '@emotion/styled';
import { Color, PrimaryButton, TextInputField } from '@liveoptics/react-ui';
import { useRequest } from 'ahooks';
import React, { useCallback, useState } from 'react';
import { setDeal } from '../../../api/api';
import { Error } from '../../../enums';
import { VerifyOwnerState } from '../../../enums/VerifyOwnerState';
import TextButton from '../../buttons/TextButton';
import { FormError } from '../form-error/FormError';

export interface SetDealFormProps {
  projectId: number;
  defaultDealId?: string;
  onCancel: () => void;
  onSuccess: (state: VerifyOwnerState, email?: string) => void;
}

const Form = styled.form`
  padding: 26px;
  color: ${Color.coolGray700};
  > p {
    font: 600 16px/20px Open Sans;
    margin: 0;
  }
`;

const FormContent = styled.div`
  padding-top: 24px;
  label {
    display: none;
  }
  > div:not(:last-child) {
    margin-bottom: 22px;
  }
`;

const ModalActions = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: baseline;
  padding-top: 32px;
  a {
    margin-right: 24px;
  }
`;

export const SetDealForm = (props: SetDealFormProps) => {
  const [dealId, setDealId] = useState<string>(props.defaultDealId ?? '');
  const [isValidDealId, setIsValidDealId] = useState<boolean>(props.defaultDealId ? true : false);
  const [errorMessage, setErrorMessage] = useState('');

  const setDealRequest = useRequest(setDeal, {
    manual: true,
    debounceWait: 500,
  });

  const handleDealIdChange = useCallback((e: React.FormEvent<HTMLInputElement>) => {
    setErrorMessage('');
    const dealId = e.currentTarget.value;
    const validDealId = /^(?!\s)+[^\\\/:*?"<>|]{0,50}(?<!\s)$/.test(dealId);
    setDealId(dealId);
    setIsValidDealId(validDealId);
  }, []);

  const handleSaveClick = useCallback(
    (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e?.preventDefault();
      setErrorMessage('');
      setDealRequest
        .runAsync(props.projectId, dealId.trim())
        .then(() => {
          props.onSuccess(VerifyOwnerState.SetDeal);
        })
        .catch(() => {
          setErrorMessage(Error.SetDeal);
        });
    },
    [dealId, props, setDealRequest]
  );

  return (
    <Form>
      <p>Assign Deal ID To This Project.</p>
      <FormContent>
        {errorMessage ? <FormError error={errorMessage} /> : <></>}
        <TextInputField
          label=""
          placeholder="Enter Deal ID"
          value={dealId}
          status={dealId === '' || isValidDealId ? 'active' : 'invalid'}
          validationMessage={
            'Please enter a valid ID, not starting or ending with spaces, and with maximum 50 characters, except \\/:*?"<>|'
          }
          onChange={handleDealIdChange}
        />
      </FormContent>
      <ModalActions>
        <TextButton onClick={props.onCancel}>Cancel</TextButton>
        <PrimaryButton
          loading={setDealRequest.loading}
          loadingText="Loading..."
          disabled={(!props.defaultDealId && dealId === '') || !isValidDealId}
          clickFn={handleSaveClick}
        >
          Save
        </PrimaryButton>
      </ModalActions>
    </Form>
  );
};
